/**
 * Where a quest mark belongs, worked out from what the step is waiting for.
 *
 * Kept apart from the interface that draws it because it is the whole of the
 * behaviour and none of the drawing: which of a wood of trees, or which of the
 * two people who could be meant, is arithmetic worth being able to test.
 */

import type { QuestStepWire } from '@inverse/protocol';

export interface Somewhere {
  x: number;
  z: number;
  plane: number;
  /**
   * Whoever is standing here, when the place is a person rather than a spot.
   *
   * Carried so the mark can be hung on them and follow them: worked out on a
   * timer, the arrow moved in half second jumps behind somebody walking, which
   * is the one target that never holds still.
   */
  entity?: number;
}

/** What the world can be asked, so this needs to know nothing about how. */
export interface Around {
  /** Everybody of a kind standing in the world. */
  characters: (defId: number) => Somewhere[];
  /** Every piece of scenery of a kind. */
  scenery: (objectId: number) => Somewhere[];
  /** Every piece of scenery that yields one thing, by gathering at it. */
  yielding: (itemId: number) => number[];
  /** Every piece of scenery one thing is made at: a fire, a furnace, an anvil. */
  making: (itemId: number) => number[];
  /** Everything of a kind lying on the ground. */
  piles: (itemId: number) => Somewhere[];
  /** How many of one thing is being carried. */
  carrying: (itemId: number) => number;
}

/**
 * The one place a mark should sit for this step, or nothing.
 *
 * While the step is waiting to be carrying something, the mark belongs where
 * that thing comes from; once it is carried, at whoever asked for it. Worked
 * out from what is in the bag every time rather than remembered, so dropping
 * one sends the mark back to the trees.
 *
 * All of that is what happens when nobody has said. A step that names somebody,
 * a piece of scenery, a thing to go and find, or a spot on the map points there
 * instead, whatever it is waiting for.
 */
export function questTarget(
  step: QuestStepWire,
  from: Somewhere,
  around: Around,
): Somewhere | null {
  const places: Somewhere[] = [];

  // Being short of what the step waits to be carrying comes before everything,
  // because it is the part being done now. "Fetch three logs and bring them to
  // Ardor" points at a tree while logs are wanted and at Ardor once they are
  // carried, and it is one step, not two.
  const wanted = Math.max(1, step.needsCount);
  const short = step.needsItem > 0 && around.carrying(step.needsItem) < wanted;

  if (short) places.push(...sourcesOf(step.needsItem, around));

  // Then whatever somebody named, which is an instruction.
  if (places.length === 0) {
    if (step.npcId > 0) places.push(...around.characters(step.npcId));
    if (step.objectId > 0) places.push(...around.scenery(step.objectId));
    if (step.itemId > 0) places.push(...sourcesOf(step.itemId, around));
    if (step.tx !== 0 || step.tz !== 0) {
      places.push({ x: step.tx + 0.5, z: step.tz + 0.5, plane: step.plane });
    }
  }

  // And failing all of that, wherever the thing it waits to have done is done:
  // the nearest tree for a log to chop, the nearest fire for a fish to cook.
  if (places.length === 0 && step.deed.length > 0 && step.deedOf > 0) {
    places.push(...sourcesOf(step.deedOf, around));
    places.push(...around.scenery(step.deedOf));
  }

  // The nearest of them. A mark on every tree in a wood is decoration; a mark
  // on the one that is closest is a direction.
  let nearest: Somewhere | null = null;
  let closest = Infinity;

  for (const place of places) {
    const away = Math.hypot(place.x - from.x, place.z - from.z);
    if (away >= closest) continue;

    closest = away;
    nearest = place;
  }

  return nearest;
}

/**
 * Everywhere one thing can be got: gathered, made, or already lying about.
 *
 * Gathering first because that is usually the answer, then the places it is
 * made at, then anything on the floor. All of them go into the same list and
 * the nearest wins, which is what somebody looking for one would do.
 */
function sourcesOf(itemId: number, around: Around): Somewhere[] {
  if (itemId <= 0) return [];

  const found: Somewhere[] = [];
  for (const objectId of around.yielding(itemId)) found.push(...around.scenery(objectId));
  for (const objectId of around.making(itemId)) found.push(...around.scenery(objectId));

  found.push(...around.piles(itemId));
  return found;
}
